import { getCoreSkillCount, MAX_CORE_SKILLS } from "@/api/skills";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";

interface CoreSlotsPillProps {
    organizationId: number;
    organizationName: string | null;
}

export function CoreSlotsPill({
    organizationId,
    organizationName,
}: CoreSlotsPillProps) {
    const { data, isLoading, isError } = useQuery({
        queryKey: ["admin-skills-core-count", organizationId],
        queryFn: () => getCoreSkillCount(organizationId),
        retry: false,
    });

    const orgLabel = organizationName ?? "this org";

    if (isError) {
        return (
            <span
                role="status"
                aria-label={`Core skill slots unavailable in ${orgLabel}`}
                title="The core skill count could not be loaded"
                className="border-border text-muted-foreground inline-flex items-center gap-1.5 rounded-full border bg-transparent px-2.5 py-1 text-xs tabular-nums"
            >
                <span
                    aria-hidden
                    className="bg-muted-foreground/50 size-1.5 rounded-full"
                />
                <span className="font-medium">
                    &mdash;
                    <span className="text-muted-foreground/70 mx-0.5 font-normal">
                        /
                    </span>
                    {MAX_CORE_SKILLS}
                </span>
                <span className="text-muted-foreground hidden sm:inline">
                    core slots
                </span>
            </span>
        );
    }

    if (isLoading || !data) return null;

    const used = data.coreCount;
    const atCap = used >= MAX_CORE_SKILLS;
    const approachingCap = used >= MAX_CORE_SKILLS - 1 && !atCap;

    return (
        <span
            role="status"
            aria-label={`${used} of ${MAX_CORE_SKILLS} core skill slots used in ${orgLabel}`}
            title={`Core skills pinned in ${orgLabel}`}
            className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs tabular-nums",
                atCap
                    ? "border-brand-copper/40 bg-brand-copper/10 text-brand-copper"
                    : approachingCap
                      ? "border-brand-copper/25 bg-brand-copper/5 text-brand-copper"
                      : "border-border text-muted-foreground bg-transparent"
            )}
        >
            <span
                aria-hidden
                className={cn(
                    "size-1.5 rounded-full",
                    atCap || approachingCap
                        ? "bg-brand-copper"
                        : "bg-muted-foreground/50"
                )}
            />
            <span className="font-medium">
                {used}
                <span className="text-muted-foreground/70 mx-0.5 font-normal">
                    /
                </span>
                {MAX_CORE_SKILLS}
            </span>
            <span className="text-muted-foreground hidden sm:inline">
                core slots
            </span>
        </span>
    );
}
